import React, { useCallback, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import CheckboxBlock from '../../../../../../features/components/Form/CheckboxBlock'
import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { updateCampaign, clearUpdateCampaign } from '../../../../../../modules/actions/campaigns'
import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../../../modules/selectors/campaigns'
import { selectedGoogleAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { MAXIMIZE_CONVERSION_VALUE, TARGET_ROAS } from '../../../../../Multiplatform/CampaignForms/fields'
import { GOOGLE_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const TargetRoasSectionForm = ({ editItemData: campaign, title, ...formProps }) => {
  const dispatch = useDispatch()

  const campaignWasUpdated = useSelector(campaignWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const targetRoas = campaign[MAXIMIZE_CONVERSION_VALUE][TARGET_ROAS]

  // if target roas value exist (or better than 0), then pre-open section
  const [checkboxBlockOpen, setCheckboxBlockOpen] = useState(!!targetRoas)

  const handleSubmit = useCallback(
    values => {
      const updateCampaignData = {
        account: selectedAdAccountId,
        [MAXIMIZE_CONVERSION_VALUE]: {
          [TARGET_ROAS]: checkboxBlockOpen ? values[TARGET_ROAS] : 0
        },
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateCampaign(updateCampaignData, campaign.id, GOOGLE_PLATFORM))
    },
    [dispatch, campaign.id, checkboxBlockOpen, title, selectedAdAccountId]
  )

  const initialValues = useMemo(
    () => ({
      [TARGET_ROAS]: String(targetRoas) || ''
    }),
    [targetRoas]
  )

  const validationSchema = Yup.object({
    ...(checkboxBlockOpen && {
      [TARGET_ROAS]: Yup.string()
        .required('Target ROAS required')
        .test({
          message: 'Please set the ROAS to a number greater than 0',
          test: value => Number(value) > 0
        })
    })
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={campaignWasUpdated}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <CheckboxBlock
        id="checkbox_set_target_roas"
        title="Set target ROAS"
        checked={checkboxBlockOpen}
        onCheck={() => {
          setCheckboxBlockOpen(!checkboxBlockOpen)
        }}
        isLarge
      >
        <Field
          type="number"
          formik={purifiedFormik}
          name={TARGET_ROAS}
          placeholder="Target ROAS"
          autoComplete="off"
          inputMode="numeric"
        />
      </CheckboxBlock>
    </EditForm>
  )
}

export default TargetRoasSectionForm
